import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

// StatusChip — the shared lifecycle-state renderer (P10 §2.2/§2.4, story
// 2-141). "Lifecycle renderer for templates/inspections/actions" — the
// primitive holds NO module-specific transition rules; a module resolves its
// own status vocabulary (nine inspection states, four template states, three
// share-link states, whatever the next module invents) down to ONE of the
// five values below, plus the display label it wants shown. That mapping —
// domain status → tone — already exists independently in FOUR places today
// (`TemplateCard`'s `STATUS_META`, `InspectionLifecycleBar`'s inline ternary,
// `ReviewDetailPage`'s inline ternary, `VersionPanel`'s `STATUS_TONE`), all
// choosing from the exact same five-tone set `Badge` already defines. That
// repetition IS the generalization: `StatusChip` does not invent a sixth
// vocabulary, it takes the one every module already converges on and makes
// its presentation — icon, weight, live-region behaviour — structural
// instead of re-derived per call site.
//
// ── Why a fixed tone set generalizes across modules with genuinely
//    different status lists ──────────────────────────────────────────────
// Inspections have nine states, templates four, share links three, and no
// two modules' raw enums overlap by name. A closed enum of the domain
// strings themselves ("draft" | "in_review" | "submitted" | "expired" | …)
// would not generalize — it would just be a union of every module's private
// vocabulary pasted into one file, which is the opposite of "holds no
// module-specific knowledge". Tone is the thing that DOES generalize: every
// module's status already answers "is this a quiet default, a positive
// outcome, a thing needing attention, an error/terminal-bad state, or an
// unremarkable/inactive one" — which is exactly Badge's five tones. So the
// module keeps its own status string (for `value`/`data-status`, matching
// `InspectionLifecycleBar`'s existing `data-status` contract) and picks one
// of five tones for it, same as it already does today, just through one
// named primitive instead of four hand-rolled maps.
//
// ── WCAG 1.4.1 (state must not rely on colour alone) ───────────────────────
// Every tone gets a FIXED, distinct icon — never a per-consumer choice —
// so two different tones are never distinguishable by hue only. `weight`
// (subtle tint vs bold solid fill, the same axis `Badge`/`VersionPanel`
// already use for a same-tone-different-meaning case like archived vs
// superseded) is the second, independent visual axis. Verified by rendering
// several tones together and viewing them under `filter: grayscale(100%)` —
// see the "StatusChip" gallery Section and this story's own verification
// notes; the label text is also always present as the accessible name, so a
// screen reader is never dependent on tone or icon either.
export type StatusChipTone = 'neutral' | 'primary' | 'success' | 'warning' | 'destructive';
export type StatusChipWeight = 'subtle' | 'bold';

const TONE_VAR: Record<StatusChipTone, string> = {
  neutral: '--muted-foreground',
  primary: '--primary',
  success: '--success',
  warning: '--warning',
  destructive: '--destructive',
};

// One glyph per tone, fixed — never a prop, so a consumer cannot accidentally
// reintroduce colour-only differentiation by picking a look-alike icon for
// two different tones.
const TONE_ICON: Record<StatusChipTone, typeof CheckCircleIcon> = {
  neutral: MinusCircleIcon,
  primary: ArrowPathIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  destructive: XCircleIcon,
};

export interface StatusChipProps {
  tone: StatusChipTone;
  /** `subtle` (default): tinted text on a faint tint. `bold`: solid fill, white text. Second, colour-independent axis alongside the icon. */
  weight?: StatusChipWeight;
  /** The display text — REQUIRED, and always the accessible name. Caller-resolved: this component never derives a label from a domain status string. */
  label: string;
  /** The module's own raw status string, for `data-status` — never read for styling, only for tests/tooling to key off (matches `InspectionLifecycleBar`'s pre-existing `data-status` contract). */
  value?: string;
  className?: string;
  'data-testid'?: string;
}

export function StatusChip({
  tone,
  weight = 'subtle',
  label,
  value,
  className = '',
  'data-testid': testId,
}: StatusChipProps): JSX.Element {
  const v = TONE_VAR[tone];
  const Icon = TONE_ICON[tone];
  const style =
    weight === 'bold'
      ? { color: '#fff', backgroundColor: `hsl(var(${v}))`, borderColor: `hsl(var(${v}))` }
      : {
          color: `hsl(var(${v}))`,
          backgroundColor: `hsl(var(${v}) / 0.1)`,
          borderColor: `hsl(var(${v}) / 0.25)`,
        };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-label font-semibold uppercase tracking-wide ${className}`}
      style={style}
      data-status={value}
      data-testid={testId}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </span>
  );
}
